import { Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { QuestionContainer, QuestionLabel } from "../../get-compliant/survey/QuestionContainer";
import { SurveyQuestionProps } from "./RadioSelectionQuestion";
import { SurveyButton } from "./SurveyButton";
import { ConfirmDialog } from "../../dialog";
import clsx from "clsx";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import { ButtonVariant } from "@truevault/aquilo/src/components/utils/types";
import { ToolTip as AquiloToolTip } from "@truevault/aquilo";

export type MultipleChoiceQuestionOption = {
  value: string;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
  confirmation?: React.ReactNode;
  autoAnswered?: boolean;
};

type Props = {
  options: MultipleChoiceQuestionOption[];
  useNewDesign?: boolean;
  onlyShowInlineAnswerButtons?: boolean; // only applicable if `useNewDesign` is true
} & SurveyQuestionProps<string>;

export const useLocalAnswer = (answer) => {
  const [localAnswer, setLocalAnswer] = useState(answer);

  useEffect(() => {
    setLocalAnswer(answer);
  }, [answer]);

  return [localAnswer, setLocalAnswer];
};

export const MultipleChoiceQuestion: React.FC<Props> = ({
  question,
  helpText,
  description,
  recommendation,
  answer,
  setAnswer,
  disabled,
  disabledTooltip,
  options,
  header,
  loading,
  useNewDesign,
  onlyShowInlineAnswerButtons,
}) => {
  const [localAnswer, setLocalAnswer] = useLocalAnswer(answer);
  const [settingAnswer, setSettingAnswer] = useState(false);

  const handleClick = useCallback(
    async (newVal) => {
      if (disabled) return;

      setSettingAnswer(true);

      try {
        await setAnswer(newVal);
        setLocalAnswer(newVal);
      } finally {
        setSettingAnswer(false);
      }
    },
    [disabled, setAnswer],
  );

  if (useNewDesign) {
    let useExtendedWidthForInlineAnswerButtons =
      options.map((o) => o.label.length).reduce((a, b) => a + b, 0) > 5;

    return (
      <div className="tw-boundary">
        {!onlyShowInlineAnswerButtons && (
          <div>
            {/* For now, hide `header` since it's not currently in the Figma designs */}
            {/* {header && (
              <div>
              {header}
              </div>
              )} */}
            <div className="text-[30px] font-bold text-neutral-800 text-left leading-[45px]">
              {question}
            </div>
            {/* {description && (
              <div>
              {description}
              </div>
              )} */}
            {helpText && (
              <div className="mt-[24px] text-[20px] text-neutral-700 text-left">{helpText}</div>
            )}
          </div>
        )}
        <div
          className={clsx(
            "flex",
            onlyShowInlineAnswerButtons
              ? useExtendedWidthForInlineAnswerButtons
                ? "max-w-[290px]"
                : "max-w-[120px]"
              : "max-w-[250px]",
            !onlyShowInlineAnswerButtons && "mx-auto mt-[36px] gap-[12px]",
          )}
          style={!onlyShowInlineAnswerButtons ? { flexDirection: "column" } : null}
        >
          {options.map((opt, idx) => {
            const key = `mult-choice-answer-${opt.value}`;
            let button = (
              <MultipleChoiceQuestionButton
                key={key}
                option={opt}
                settingAnswer={settingAnswer}
                localAnswer={localAnswer}
                loading={loading}
                rowDisabled={disabled}
                rowDisabledTooltip={disabledTooltip}
                onClick={handleClick}
                useNewDesign={true}
                newDesignButtonVariant={(() => {
                  if (!onlyShowInlineAnswerButtons) return undefined; // use default button variant
                  if (idx === 0) return "leftRounded";
                  if (idx === options.length - 1) return "rightRounded";
                  return "noRound";
                })()}
              />
            );

            if (onlyShowInlineAnswerButtons) {
              return (
                <div className="flex-1" key={key}>
                  {button}
                </div>
              );
            } else {
              return button;
            }
          })}
        </div>
        {recommendation && !onlyShowInlineAnswerButtons && (
          <div className="mx-auto text-primary-700 max-w-[50ch] mt-[12px] flex items-baseline gap-[5px]">
            <div>{LightbulbIcon}</div>
            <div>{recommendation}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <QuestionContainer>
      <QuestionLabel
        label={question}
        helpText={helpText}
        header={header}
        description={description}
        recommendation={recommendation}
      />
      <div className="survey-question--mult-choice">
        {options.map((opt) => {
          const key = `mult-choice-answer-${opt.value}`;
          return (
            <MultipleChoiceQuestionButton
              key={key}
              option={opt}
              settingAnswer={settingAnswer}
              localAnswer={localAnswer}
              loading={loading}
              rowDisabled={disabled}
              rowDisabledTooltip={disabledTooltip}
              onClick={handleClick}
            />
          );
        })}
      </div>
    </QuestionContainer>
  );
};

type MultipleChoiceQuestionButtonProps = {
  localAnswer: string;
  settingAnswer: boolean;
  rowDisabled: boolean;

  onClick: (newVal: string) => void;

  rowDisabledTooltip?: string;
  option: MultipleChoiceQuestionOption;
  loading?: boolean;

  useNewDesign?: boolean;
  newDesignButtonVariant?: ButtonVariant;
};
const MultipleChoiceQuestionButton: React.FC<MultipleChoiceQuestionButtonProps> = ({
  option: opt,
  localAnswer,
  settingAnswer,
  rowDisabled,
  rowDisabledTooltip,
  loading,
  onClick,
  useNewDesign,
  newDesignButtonVariant,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const selected = opt.autoAnswered || localAnswer === opt.value;

  const isDisabled = opt.disabled || rowDisabled;
  let tooltip = undefined;
  if (rowDisabled && rowDisabledTooltip) {
    tooltip = rowDisabledTooltip;
  }
  if (opt.disabled && opt.disabledTooltip) {
    tooltip = opt.disabledTooltip;
  }

  const hasConfirmation = Boolean(opt.confirmation);

  const handleClick = () => {
    if (hasConfirmation) {
      setConfirmOpen(true);
    } else {
      onClick(opt.value);
    }
  };

  const isLoading = loading || settingAnswer;
  const isDisabledSelected = isDisabled && selected;

  const optionEl = (
    <>
      <SurveyButton
        disabled={isDisabled || isLoading}
        loading={isLoading && selected}
        color={selected ? "primary" : "default"}
        onClick={handleClick}
        useNewDesign={useNewDesign}
        newDesignButtonVariant={newDesignButtonVariant}
        disabledSelected={isDisabledSelected}
      >
        {opt.label}
      </SurveyButton>
      {hasConfirmation && (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            onClick(opt.value);
          }}
          confirmTitle={"Are you sure?"}
          contentText={opt.confirmation}
          confirmText="Yes"
        />
      )}
    </>
  );

  if (useNewDesign && isDisabled) {
    return (
      <AquiloToolTip text={tooltip} xOffset={-10} yOffset={70} maxWidth={"350px"}>
        {optionEl}
      </AquiloToolTip>
    );
  } else if (isDisabled) {
    return (
      <Tooltip
        placement="bottom-start"
        title={tooltip}
        componentsProps={{
          tooltip: {
            sx: { maxWidth: 220 },
          },
        }}
      >
        <span>{optionEl}</span>
      </Tooltip>
    );
  } else {
    return optionEl;
  }
};
